import { todayLocal } from "./money.ts";
import type { AccountBalance, Category, IncomeSource, OperationDraft } from "./types.ts";

export interface ParseResult {
  draft: OperationDraft;
  missing: "amount" | "account" | "toAccount" | null;
}

function normalize(value: string): string {
  return value.toLocaleLowerCase("ru").replace(/ё/g, "е").replace(/\s+/g, " ").trim();
}

function detectRubles(text: string): bigint | null {
  const fixed: Array<[RegExp, bigint]> = [
    [/пятнадцать тысяч девятьсот сорок семь/, 15_947n],
    [/две двести/, 2_200n],
    [/двадцатк/, 20_000n],
    [/трешк/, 3_000n],
    [/пятак/, 5_000n],
  ];
  for (const [pattern, amount] of fixed) if (pattern.test(text)) return amount;
  const kilo = text.match(/(\d+[.,]?\d*)\s*к(?:\s|$)/);
  if (kilo) return BigInt(Math.round(Number(kilo[1].replace(",", ".")) * 1000));
  const numbers = [...text.matchAll(/\b\d[\d\s]*\b/g)]
    .map((match) => match[0].replace(/\s/g, ""))
    .filter((value) => /^\d+$/.test(value));
  return numbers.length ? BigInt(numbers.at(-1)!) : null;
}

function accountPatterns(account: AccountBalance): RegExp[] {
  const name = normalize(account.name);
  const patterns = [new RegExp(name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))];
  if (/сбер/.test(name)) patterns.push(/сбер(?:а|банк)?/);
  if (/т-?банк|тиньк/.test(name)) patterns.push(/(?:тиньков|тинек|тинькофф|тбанк|т-банк)/);
  if (/втб/.test(name)) patterns.push(/втб/);
  if (/альф/.test(name)) patterns.push(/альф(?:а|у)/);
  if (/ozon|озон/.test(name)) patterns.push(/(?:ozon|озон)(?:а)?/);
  if (/налич/.test(name) || account.kind === "cash") patterns.push(/налич(?:ными|ные|ку|ки)?/);
  return patterns;
}

function matchingAccounts(text: string, accounts: AccountBalance[]): AccountBalance[] {
  return accounts
    .map((account) => ({
      account,
      index: Math.min(
        ...accountPatterns(account)
          .map((pattern) => text.search(pattern))
          .filter((index) => index >= 0),
      ),
    }))
    .filter(({ index }) => Number.isFinite(index))
    .sort((a, b) => a.index - b.index)
    .map(({ account }) => account);
}

function findNamed<T extends { id: string; name: string }>(items: T[], names: string[]): T | undefined {
  return items.find((item) => names.some((name) => normalize(item.name).includes(normalize(name))));
}

export function parsePhrase(
  source: string,
  accounts: AccountBalance[],
  categories: Category[],
  incomeSources: IncomeSource[],
): ParseResult {
  const text = normalize(source);
  const amountRubles = detectRubles(text);
  const amountMinor = (amountRubles ?? 0n) * 100n;
  const matches = matchingAccounts(text, accounts.filter((account) => !account.archivedAt));
  const base = {
    amountMinor,
    occurredOn: todayLocal(),
    accountId: matches[0]?.accountId ?? "",
    originalText: source,
    confidence: "Высокая" as const,
  };

  if (/снял|снять/.test(text) && /налич/.test(text)) {
    const cash = accounts.find((account) => account.kind === "cash" && !account.archivedAt);
    const bank = matches.find((account) => account.kind !== "cash");
    const draft: OperationDraft = {
      ...base,
      type: "transfer",
      accountId: bank?.accountId ?? "",
      toAccountId: cash?.accountId ?? null,
      description: "Снятие наличных",
    };
    return { draft, missing: missingField(draft) };
  }
  if (/внес|положил налич/.test(text)) {
    const cash = accounts.find((account) => account.kind === "cash" && !account.archivedAt);
    const bank = [...matches].reverse().find((account) => account.kind !== "cash");
    const draft: OperationDraft = {
      ...base,
      type: "transfer",
      accountId: cash?.accountId ?? "",
      toAccountId: bank?.accountId ?? null,
      description: "Внесение наличных",
    };
    return { draft, missing: missingField(draft) };
  }
  if (/перевел|перевод/.test(text)) {
    const draft: OperationDraft = {
      ...base,
      type: "transfer",
      toAccountId: matches.at(-1)?.accountId ?? null,
      description: "Перевод между своими счетами",
    };
    return { draft, missing: missingField(draft) };
  }
  if (/получил|зарплат|\bзп\b|за мою работу/.test(text)) {
    const work = /за мою работу/.test(text);
    const sourceName = work ? "Оплата за работу" : "Зарплата";
    const draft: OperationDraft = {
      ...base,
      type: "income",
      accountId: matches.at(-1)?.accountId ?? "",
      incomeSourceId: findNamed(incomeSources, [sourceName, "Другое"])?.id ?? null,
      description: work ? "Оплата за работу" : "Зарплата",
      counterparty: /рома/.test(text) ? "Рома" : /завод/.test(text) ? "Завод" : null,
    };
    return { draft, missing: missingField(draft) };
  }

  const categoryNames = /заправ|топлив/.test(text)
    ? ["Топливо"]
    : /стекл/.test(text)
      ? ["Ремонт автомобиля"]
      : /chatgpt/.test(text)
        ? ["ИИ-сервисы"]
        : /пятероч/.test(text)
          ? ["Продукты"]
          : /столов/.test(text)
            ? ["Столовая и кафе"]
            : /патч/.test(text)
              ? ["Подарки и близкие"]
              : /доставк/.test(text)
                ? ["Доставка и логистика"]
                : /штан/.test(text)
                  ? ["Одежда и обувь"]
                  : /обучен|маркетинг/.test(text)
                    ? ["Маркетинг"]
                    : ["Другое"];
  const category = findNamed(categories, categoryNames) ?? findNamed(categories, ["Другое"]);
  const draft: OperationDraft = {
    ...base,
    type: "expense",
    categoryId: category?.id ?? null,
    description: source,
    confidence: categoryNames[0] === "Другое" ? "Средняя" : "Высокая",
  };
  return { draft, missing: missingField(draft) };
}

export function missingField(draft: OperationDraft): ParseResult["missing"] {
  if (draft.amountMinor <= 0n) return "amount";
  if (!draft.accountId) return "account";
  if (draft.type === "transfer" && !draft.toAccountId) return "toAccount";
  return null;
}
